import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getSubject, getLesson, generateRounds, quizFallbackRounds } from "../lib/content";
import { useGame } from "../context/GameContext";
import api from "../lib/api";
import ChoiceRound from "../games/ChoiceRound";
import MemoryGame from "../games/MemoryGame";
import MusicKeys from "../games/MusicKeys";
import DrawCanvas from "../games/DrawCanvas";
import RewardModal from "../components/RewardModal";
import SquishyButton from "../components/SquishyButton";
import Mascot from "../components/Mascot";
import Confetti from "../components/Confetti";
import { speak } from "../lib/speech";
import { X } from "lucide-react";

const SELF_CONTAINED = ["draw", "music-free", "music-play"];

export default function Lesson() {
  const { subjectId, lessonId } = useParams();
  const navigate = useNavigate();
  const { profile, completeLesson } = useGame();
  const subject = getSubject(subjectId);
  const lesson = getLesson(subjectId, lessonId);
  const isSelf = lesson && SELF_CONTAINED.includes(lesson.type);

  const [rounds, setRounds] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("intro"); // intro | play | done
  const [correctCount, setCorrectCount] = useState(0);
  const [flash, setFlash] = useState(false);
  const [reward, setReward] = useState(null);
  const [runId, setRunId] = useState(0);
  const startTime = useRef(Date.now());

  const buildRounds = async () => {
    if (!lesson || !profile || SELF_CONTAINED.includes(lesson.type)) return;
    if (lesson.ai) {
      setAiLoading(true);
      setRounds([]);
      try {
        const { data } = await api.post("/ai/lesson", {
          subject: subjectId, skill: lesson.skill || subjectId, age: profile.age,
        });
        const aiRounds = Array.isArray(data?.rounds) ? data.rounds : [];
        setRounds(aiRounds.length ? aiRounds : quizFallbackRounds(lesson.skill || subjectId, profile.age));
      } catch {
        setRounds(quizFallbackRounds(lesson.skill || subjectId, profile.age));
      } finally {
        setAiLoading(false);
      }
    } else {
      setRounds(generateRounds(lesson.type, profile.age));
    }
  };

  useEffect(() => { buildRounds(); startTime.current = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, profile]);

  useEffect(() => {
    if (phase === "intro" && lesson) { const t = setTimeout(() => speak(lesson.intro), 500); return () => clearTimeout(t); }
  }, [phase, lesson]);

  if (!subject || !lesson || !profile) return null;

  const finishLesson = async (correct, totalArg) => {
    setPhase("done");
    const total = totalArg ?? rounds.length;
    const ratio = total ? correct / total : 1;
    const stars = ratio >= 0.9 ? 3 : ratio >= 0.6 ? 2 : 1;
    const coins = correct * 5 + stars * 5;
    const diamonds = stars === 3 ? 1 : 0;
    const duration = Math.round((Date.now() - startTime.current) / 1000);
    await completeLesson({
      subject: subjectId, lesson_id: lessonId, lesson_title: lesson.title,
      score: correct, total: total || 1, coins, stars, diamonds, duration_seconds: duration,
    });
    setReward({ stars, coins, diamonds });
  };

  const onRoundComplete = (firstTry) => {
    const nextCorrect = correctCount + (firstTry ? 1 : 0);
    setCorrectCount(nextCorrect);
    setFlash(true);
    setTimeout(() => setFlash(false), 700);
    if (index + 1 >= rounds.length) finishLesson(nextCorrect);
    else setIndex((i) => i + 1);
  };

  const replay = () => {
    setReward(null); setIndex(0); setCorrectCount(0); setPhase("play");
    buildRounds(); setRunId((r) => r + 1);
    startTime.current = Date.now();
  };

  const current = rounds[index];
  const total = rounds.length;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${subject.color}22, #F0F9FF)` }}>
      <Confetti show={flash} count={18} />

      <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3 backdrop-blur-md bg-white/50">
        <button data-testid="lesson-close-btn" onClick={() => navigate(`/vak/${subjectId}`)}
                className="rounded-full bg-white p-2 shadow text-slate-500"><X className="h-5 w-5" /></button>
        {isSelf ? (
          <span className="flex-1 font-display font-bold text-slate-600">{lesson.icon} {lesson.title}</span>
        ) : (
          <>
            <div className="h-4 flex-1 overflow-hidden rounded-full bg-white/70 shadow-inner">
              <motion.div className="h-full rounded-full" style={{ background: subject.color }}
                          animate={{ width: `${(index / Math.max(total, 1)) * 100}%` }} />
            </div>
            <span className="font-display font-bold text-slate-600" data-testid="lesson-progress">{index}/{total}</span>
          </>
        )}
      </div>

      <div className="mx-auto flex max-w-lg flex-col items-center px-4 pb-16 pt-6">
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div key="intro" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-6 text-center">
              <Mascot mood="idle" size={150} message={lesson.intro} />
              <div className="text-6xl">{lesson.icon}</div>
              <h1 className="font-display text-3xl font-bold text-slate-800">{lesson.title}</h1>
              <SquishyButton data-testid="lesson-start-btn" onClick={() => setPhase("play")}
                             color="#4ADE80" edge="#16A34A" className="text-2xl px-10">Start ➜</SquishyButton>
            </motion.div>
          )}

          {phase === "play" && isSelf && (
            <motion.div key={`self-${runId}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full">
              {lesson.type === "draw"
                ? <DrawCanvas onFinish={(c, t) => finishLesson(c, t)} />
                : <MusicKeys mode={lesson.type === "music-play" ? "play" : "free"} onFinish={(c, t) => finishLesson(c, t)} />}
            </motion.div>
          )}

          {phase === "play" && !isSelf && (aiLoading || rounds.length === 0) && (
            <motion.div key="ai-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-6 py-10 text-center">
              <Mascot mood="idle" size={140} message="Luna maakt speciale vragen voor jou..." />
              <motion.div className="text-5xl" animate={{ rotate: [0, 12, -12, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>✨</motion.div>
              <p data-testid="ai-loading-text" className="font-display text-xl font-bold text-slate-600">Luna maakt vragen voor jou…</p>
            </motion.div>
          )}

          {phase === "play" && !isSelf && !aiLoading && current && (
            <motion.div key={`round-${index}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="w-full">
              {current.kind === "memory"
                ? <MemoryGame round={current} onComplete={onRoundComplete} />
                : <ChoiceRound round={current} onComplete={onRoundComplete} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <RewardModal
        open={!!reward}
        stars={reward?.stars || 0}
        coins={reward?.coins || 0}
        diamonds={reward?.diamonds || 0}
        message="Goed gedaan! Je hebt de les gehaald!"
        onClose={() => navigate(`/vak/${subjectId}`)}
        onReplay={replay}
      />
    </div>
  );
}
